import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban } from 'lucide-react';

import {
  listVisitorBans,
  deleteVisitorBan,
  type VisitorBan,
} from '../api/bans';
import {
  getTenantSecurityPolicy,
  patchTenantSecurityPolicy,
  type BanPolicy,
} from '../api/tenant_policy';
import { getApiClient } from '../api/factory';
import { useAuthStore } from '../store/auth';
import { useT, type TFn } from '../i18n';

// ANX-046 — admin-only страница банов visitor'ов внутри /settings.
// Две секции: переключатель ban-policy + таблица текущих и истекших банов
// с кнопкой «Снять».

export function VisitorBansSection() {
  const t = useT();
  const operator = useAuthStore((s) => s.session?.operator);
  const isAdmin = operator?.role === 'admin';
  const qc = useQueryClient();

  const policyQuery = useQuery({
    queryKey: ['tenant_security_policy'],
    queryFn: () => getTenantSecurityPolicy(getApiClient()),
    enabled: isAdmin,
  });
  const bansQuery = useQuery({
    queryKey: ['visitor_bans'],
    queryFn: () => listVisitorBans(getApiClient()),
    enabled: isAdmin,
  });

  const policyMu = useMutation({
    mutationFn: (banPolicy: BanPolicy) =>
      patchTenantSecurityPolicy(getApiClient(), { banPolicy }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant_security_policy'] }),
  });
  const deleteMu = useMutation({
    mutationFn: (id: string) => deleteVisitorBan(getApiClient(), id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['visitor_bans'] }),
  });

  if (!isAdmin) return null;

  const currentPolicy: BanPolicy = policyQuery.data?.banPolicy ?? 'admin_only';
  const bans = bansQuery.data ?? [];

  return (
    <section className="mt-4 rounded-md border border-slate-200 bg-white p-4 shadow-card">
      <div className="mb-3 flex items-center gap-2">
        <Ban className="h-4 w-4 text-red-600" />
        <h3 className="text-sm font-medium text-slate-900">{t('bans.heading')}</h3>
      </div>

      <label className="mb-3 block text-sm text-slate-700">
        <span className="mb-1 block text-xs text-slate-500">{t('bans.policy.label')}</span>
        <select
          value={currentPolicy}
          disabled={policyMu.isPending || policyQuery.isLoading}
          onChange={(e) => policyMu.mutate(e.target.value as BanPolicy)}
          className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100"
        >
          <option value="admin_only">{t('bans.policy.adminOnly')}</option>
          <option value="any_operator">{t('bans.policy.anyOperator')}</option>
        </select>
        <span className="ml-2 text-xs text-slate-500">{t('bans.policy.hint')}</span>
      </label>

      {bansQuery.isLoading ? (
        <p className="text-sm text-slate-500">{t('common.loading')}</p>
      ) : bans.length === 0 ? (
        <p className="text-sm text-slate-500">{t('bans.empty')}</p>
      ) : (
        <BansTable
          bans={bans}
          t={t}
          onDelete={(id) => deleteMu.mutate(id)}
          pendingId={deleteMu.isPending ? deleteMu.variables ?? null : null}
        />
      )}
    </section>
  );
}

function BansTable({
  bans,
  t,
  onDelete,
  pendingId,
}: {
  bans: VisitorBan[];
  t: TFn;
  onDelete: (id: string) => void;
  pendingId: string | null;
}) {
  const [confirmId, setConfirmId] = useState<string | null>(null);
  return (
    <div className="overflow-hidden rounded border border-slate-200">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-left text-slate-500">
          <tr>
            <th className="px-3 py-2 font-medium">{t('bans.table.visitor')}</th>
            <th className="px-3 py-2 font-medium">{t('bans.table.ip')}</th>
            <th className="px-3 py-2 font-medium">{t('bans.table.reason')}</th>
            <th className="px-3 py-2 font-medium">{t('bans.table.expiresAt')}</th>
            <th className="px-3 py-2 font-medium">{t('bans.table.status')}</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {bans.map((b) => (
            <tr key={b.id} className="border-t border-slate-100">
              <td className="px-3 py-2 font-mono text-[11px] text-slate-600">
                {b.visitorId.slice(0, 8)}…
              </td>
              <td className="px-3 py-2 font-mono text-[11px] text-slate-600">
                {b.lastIp ?? '—'}
              </td>
              <td className="px-3 py-2 text-slate-700">{b.reason || '—'}</td>
              <td className="px-3 py-2 text-slate-500">
                {b.expiresAt ? new Date(b.expiresAt).toLocaleString() : t('bans.permanent')}
              </td>
              <td className="px-3 py-2">
                {b.active ? (
                  <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-700">
                    {t('bans.status.active')}
                  </span>
                ) : (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                    {t('bans.status.expired')}
                  </span>
                )}
              </td>
              <td className="px-3 py-2 text-right">
                {confirmId === b.id ? (
                  <span className="inline-flex items-center gap-2">
                    <button
                      type="button"
                      disabled={pendingId === b.id}
                      onClick={() => onDelete(b.id)}
                      className="rounded bg-red-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      {t('bans.unban.confirm')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmId(null)}
                      className="text-[11px] text-slate-500 hover:text-slate-700"
                    >
                      {t('common.cancel')}
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmId(b.id)}
                    className="text-[11px] text-accent-600 hover:text-accent-700"
                  >
                    {t('bans.unban')}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
